import React, { useContext, useState, useEffect } from 'react';
import { number, string, func, bool, arrayOf } from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import classNames from 'classnames';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { CheckAvailability } from '@thd-olt-component-react/check-availability';
import { dataModel } from '../dataModel';
import { FULFILLMENT_METHODS } from '../constants';
import { useMajorApplianceFulfillment } from '../../hooks/useMajorApplianceFulfillment';
import { ApplianceError } from '../shared/ApplianceError/ApplianceError';
import { Discontinued } from '../shared/Discontinued/Discontinued';
import { FulfillmentPlaceholder } from './Placeholder/FulfillmentPlaceholder';
import { StoreUnavailable } from './partials/store/StoreUnavailable';
import '../fulfillment.scss';
import { isMobile } from '../helper/utils';

const MajorApplianceThreeTiles = (props) => {
  const {
    itemId,
    configurableApplianceItemIds,
    storeId,
    quantity,
    onChange,
    covidAlert,
    bopisMinimumThreshold,
    bopisMinimumThresholdStores,
    bopisHolidayTiming
  } = props;

  const {
    channel,
  } = useContext(ExperienceContext);
  const { storeId: defaultStoreId, storeName } = useStore();

  const cookieZipCode = typeof window !== 'undefined' && window?.cookieUtils?.readBrowserCookie('DELIVERY_ZIP');
  const [deliveryZip, setDeliveryZip] = useState(cookieZipCode);
  const { isConsumerApp } = useContext(ExperienceContext);

  useEffect(() => {
    if (isConsumerApp) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.on('NATIVE_APP.DELIVERY_ZIP', ({ output }) => {
        if (output?.deliveryZip) {
          setDeliveryZip(output?.deliveryZip);
        }
      });
    }
  }, []);
  const [{
    availabilityType,
    fulfillmentModels,
    currentFulfillment,
    someSkusHaveZeroPrice,
    loading
  }, changeFulfillment] = useMajorApplianceFulfillment({
    itemId,
    configurableApplianceItemIds,
    quantity,
    zipCode: isConsumerApp ? deliveryZip : cookieZipCode,
    storeId: storeId || defaultStoreId,
    storeName,
    channel,
    onChange,
    covidAlert,
    bopisMinimumThreshold,
    bopisMinimumThresholdStores,
    bopisHolidayTiming
  });
  const [directDeliveryTemplate = {}, storeTemplate = {}] = fulfillmentModels;

  if (loading) {
    return (
      <FulfillmentPlaceholder isMajorAppliances />
    );
  }

  if (someSkusHaveZeroPrice) {
    return (
      <StoreUnavailable itemId={itemId} isAppliance />
    );
  }

  if (availabilityType?.discontinued) {
    return (
      <Discontinued />
    );
  }
  if ((!directDeliveryTemplate.enabled && !storeTemplate.enabled)) {
    return (
      <ApplianceError
        directDelivery={directDeliveryTemplate}
      />
    );
  }
  return (
    <span data-component="MajorApplianceThreeTiles">
      <div className="fulfillment__header_title">How to Get It</div>
      <br />
      <div className="fulfillment__content-wrapper card-deck">
        {fulfillmentModels.map((fulfillmentModel, i) => {
          const wrapperClasses = classNames({
            card: true,
            u__legal: isMobile(channel),
            'mobile--margin-right': true,
            'card-container': true,
            'u__text--primary': !fulfillmentModel.enabled,
            'appliance--card-selected': fulfillmentModel.method === currentFulfillment?.method,
            'appliance--card-unselected':
              fulfillmentModel.method !== currentFulfillment?.method && fulfillmentModel.enabled,
            'appliance--card-enabled': fulfillmentModel.enabled,
            'appliance--card-unclickable':
              fulfillmentModel.method !== currentFulfillment?.method && !fulfillmentModel.enabled,
          });
          return (
            // eslint-disable-next-line
            <a
              key={i}
              type="button"
              className={wrapperClasses}
              onClick={() => {
                if ((fulfillmentModel.method === currentFulfillment?.method) || !fulfillmentModel.enabled) return;
                changeFulfillment(fulfillmentModel);
              }}
            >
              {fulfillmentModel.template}
            </a>
          );
        })}
      </div>
      {currentFulfillment?.subTemplate && (
        <div className="fulfillment__fullcontent">
          {currentFulfillment.subTemplate}
          {currentFulfillment.method === FULFILLMENT_METHODS.BOPIS && (
            <div>
              <span className="u__bold">
                Product installation and haul away are not available with appliance store pick up.
              </span>
              <span>
                {` Protection plans and parts needed for
                installation are available for purchase at the store. To pick up your
                appliance, go directly to the customer service desk. When you arrive at the store,
                please ask a store associate for assistance.
                Please ensure you have a vehicle that is able to transport a large appliance.`}
              </span>
            </div>
          )}
        </div>
      )}
    </span>
  );
};

MajorApplianceThreeTiles.displayName = 'MajorApplianceFulfillment';

MajorApplianceThreeTiles.propTypes = {
  /** An Item that has product highlights */
  itemId: string.isRequired,
  configurableApplianceItemIds: arrayOf(string),
  covidAlert: bool,
  storeId: string,
  quantity: number,
  onChange: func,
  bopisMinimumThreshold: number,
  bopisMinimumThresholdStores: string,
  bopisHolidayTiming: number
};

MajorApplianceThreeTiles.defaultProps = {
  configurableApplianceItemIds: null,
  covidAlert: false,
  quantity: 1,
  storeId: null,
  bopisMinimumThreshold: null,
  bopisMinimumThresholdStores: null,
  onChange: () => {},
  bopisHolidayTiming: null
};

// ['identifiers', 'info', 'pricing', 'availabilityType', 'fulfillment']
MajorApplianceThreeTiles.dataModel = extend(
  CheckAvailability,
  dataModel
);

export { MajorApplianceThreeTiles };
